import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { RoleGuard, AccessDenied } from '../components/RoleGuard';
import { LoadingSpinner } from '../components/LoadingStates';
import {
  Users,
  Shield,
  Video,
  Scissors,
  UserCheck,
  UserX,
  Search,
  Trash2,
  UserPlus,
  KeyRound,
  Plus,
  X,
  Share2,
} from 'lucide-react';

type RuoloOperativo = 'videomaker' | 'video_editor';

interface Utente {
  id: string;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: 'admin' | 'videomaker' | 'video_editor' | 'copywriter' | 'publisher';
  created_at?: string;
  // Email in whitelist: l'approvazione lo renderà amministratore
  will_be_admin?: boolean;
  // false = profilo di sola assegnazione, entra con l'account admin condiviso
  login_enabled?: boolean;
  // Ruolo aggiuntivo publisher: pubblica i reel e vede tutti i clienti
  is_publisher?: boolean;
  // Altri ruoli operativi coperti dalla stessa persona (chi gira e monta)
  extra_roles?: string[];
  // Etichette libere («grafico», «copywriter»…): solo descrittive
  role_tags?: string[];
}

const RUOLI_OPERATIVI: Array<{ id: RuoloOperativo; label: string; icon: React.FC<{ className?: string }> }> = [
  { id: 'videomaker', label: 'Videomaker', icon: Video },
  { id: 'video_editor', label: 'Video Editor', icon: Scissors },
];

interface SlotOccupato {
  user_id: string;
  username: string;
  first_name?: string;
  last_name?: string;
  role: string;
}

interface RigaCliente {
  client_id: string;
  company_name: string;
  status: string;
  videomaker: SlotOccupato | null;
  video_editor: SlotOccupato | null;
}

interface Statistiche {
  total_assignments: number;
  clients_without_videomaker: number;
  clients_without_editor: number;
  users_without_assignments: number;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const nomeUtente = (u: { first_name?: string; last_name?: string; username: string }) =>
  u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.username;

/*
  Resa "alla Radar" (task-radar/app/lib/screens/team_screen.dart + primitives):
  titolo in serif, descrizione bodySmall, pannelli bianchi col filetto, righe
  compatte, campi densi riempiti #F4F5F7, bottone primario giallo con testo nero,
  tag come TagBadge (velo giallo o tenue). Solo estetica: la logica non cambia.
*/
const campoTesto =
  'h-8 w-full px-2.5 py-0 leading-none text-[13px] bg-[#f4f5f7] border border-[#dde0e6] rounded-md text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#13161c] focus:bg-white transition-colors';

const bottonePrimario =
  'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md bg-[#fdf751] text-[#13161c] text-[13px] font-semibold hover:bg-[#f4ee3d] active:scale-[0.97] transition-all whitespace-nowrap flex-shrink-0';

const bottoneIcona =
  'inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:bg-[#f0f1f4] hover:text-slate-900 transition-colors flex-shrink-0';

/** Colonne della lista del team: nome, cognome, email di accesso, ruoli, clienti. */
const grigliaPersona =
  'grid grid-cols-2 sm:grid-cols-[1fr_1fr_1.2fr_1.6fr_120px] gap-2 items-center px-3 py-2';

const bottoneTesto =
  'inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-[13px] text-slate-600 hover:bg-[#f0f1f4] hover:text-slate-900 transition-colors';

/** Etichetta di sezione: labelSmall del Radar. */
const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[10.5px] font-semibold uppercase tracking-[1.1px] text-slate-400">{children}</div>
);

/** Pannello bianco col filetto (Panel del Radar). */
const Panel: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`bg-white border border-[#ebedf1] rounded-[10px] ${className}`}>{children}</div>
);

/** Tag: velo giallo con testo inchiostro, o tenue col solo filetto (TagBadge). */
const Tag: React.FC<{ children: React.ReactNode; tenue?: boolean; title?: string }> = ({
  children,
  tenue = false,
  title,
}) => (
  <span
    title={title}
    className={`inline-flex items-center gap-1 px-2 py-[2px] rounded text-[11px] whitespace-nowrap border ${
      tenue
        ? 'text-slate-600 border-[#ebedf1] font-medium'
        : 'text-[#13161c] bg-[#fdf751]/10 border-[#13161c]/45 font-semibold'
    }`}
  >
    {children}
  </span>
);

/** Titolo di sezione con conteggio e, a destra, un'azione facoltativa. */
const SezioneTitolo: React.FC<{
  titolo: string;
  conteggio?: React.ReactNode;
  sotto?: string;
  destra?: React.ReactNode;
}> = ({ titolo, conteggio, sotto, destra }) => (
  <div className="flex items-start justify-between gap-3 mb-2">
    <div className="min-w-0">
      <div className="flex items-baseline gap-2">
        <h3 className="text-[15px] font-semibold text-slate-900">{titolo}</h3>
        {conteggio !== undefined && <span className="text-xs text-slate-400">{conteggio}</span>}
      </div>
      {sotto && <p className="text-xs text-slate-500 mt-0.5">{sotto}</p>}
    </div>
    {destra}
  </div>
);

// Tendina di uno slot: mostra chi è assegnato e permette di cambiarlo.
// Definito fuori dalla pagina per non essere rimontato a ogni render.
const SelectSlot: React.FC<{
  riga: RigaCliente;
  slot: RuoloOperativo;
  opzioni: Utente[];
  // Admin che girano o montano per qualche cliente: vanno sempre offerti,
  // altrimenti non c'è modo di assegnarli a un cliente nuovo.
  admin: Utente[];
  inCorso: boolean;
  onChange: (userId: string) => void;
}> = ({ riga, slot, opzioni, admin, inCorso, onChange }) => {
  const occupante = slot === 'videomaker' ? riga.videomaker : riga.video_editor;

  // Chi occupa lo slot va sempre in elenco, anche se non compare tra le opzioni
  // (per esempio un profilo poi disattivato): senza questa voce la tendina
  // mostrerebbe "— Nessuno —" su un cliente in realtà assegnato.
  const assegnabili = [...opzioni, ...admin];
  const voci: Array<{ id: string; username: string; first_name?: string; last_name?: string }> =
    occupante && !assegnabili.some(u => u.id === occupante.user_id)
      ? [
          ...opzioni,
          {
            id: occupante.user_id,
            username: occupante.username,
            first_name: occupante.first_name,
            last_name: occupante.last_name
          }
        ]
      : opzioni;

  return (
    <select
      value={occupante?.user_id || ''}
      disabled={inCorso}
      onChange={e => onChange(e.target.value)}
      className={`${campoTesto} disabled:opacity-50 ${
        occupante ? '' : 'border-dashed text-slate-400'
      }`}
    >
      <option value="">— Nessuno —</option>
      {voci.map(u => (
        <option key={u.id} value={u.id}>
          {nomeUtente(u)}
        </option>
      ))}
      {admin.length > 0 && (
        <optgroup label="Admin">
          {admin.map(u => (
            <option key={u.id} value={u.id}>
              {nomeUtente(u)}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
};

/**
 * Ruolo publisher: è di Aldo e resta suo. Non è più un interruttore — dalla
 * dashboard non si assegna né si toglie a nessuno, si vede soltanto chi ce l'ha.
 */
const TagPublisher: React.FC = () => (
  <Tag title="Pubblica i reel e vede tutti i clienti. Ruolo fisso: non si assegna dalla dashboard.">
    <Share2 className="h-3 w-3" />
    publisher
  </Tag>
);

/**
 * I ruoli di una persona, come nel Team del Radar: solo quelli che ha davvero,
 * ognuno con la × per toglierlo, e un «+ ruolo» che apre un campo in linea
 * (Invio conferma, Esc annulla). Videomaker e Video Editor sono ruoli operativi
 * (tendine e permessi): se si scrive uno di quei due nomi il tag diventa un
 * ruolo extra vero, qualunque altra parola è un'etichetta descrittiva. Il ruolo
 * principale non ha la ×: regola i permessi di base dell'account.
 */
const RuoliPersona: React.FC<{
  utente: Utente;
  onToggleRuoloExtra: (ruolo: RuoloOperativo) => void;
  onCambiaTag: (tags: string[]) => void;
}> = ({ utente, onToggleRuoloExtra, onCambiaTag }) => {
  const [aperto, setAperto] = useState(false);
  const [testo, setTesto] = useState('');
  const extra = (utente.extra_roles ?? []) as RuoloOperativo[];
  const tags = utente.role_tags ?? [];
  const listaId = `ruoli-${utente.id}`;

  const operativoDa = (v: string): RuoloOperativo | null => {
    const k = v.trim().toLowerCase().replace(/[\s_-]+/g, '');
    if (k === 'videomaker') return 'videomaker';
    if (k === 'videoeditor') return 'video_editor';
    return null;
  };

  const conferma = () => {
    const v = testo.trim();
    setTesto('');
    setAperto(false);
    if (!v) return;
    const operativo = operativoDa(v);
    if (operativo) {
      if (utente.role !== operativo && !extra.includes(operativo)) onToggleRuoloExtra(operativo);
      return;
    }
    if (!tags.some(t => t.toLowerCase() === v.toLowerCase())) onCambiaTag([...tags, v]);
  };

  const chip = (label: string, icon: React.ReactNode, onRimuovi?: () => void, title?: string) => (
    <span
      key={label}
      title={title}
      className={`inline-flex items-center gap-1 h-7 rounded text-[11px] whitespace-nowrap border ${
        onRimuovi ? 'pl-2 pr-1' : 'px-2'
      } ${icon ? 'text-[#13161c] bg-[#fdf751]/10 border-[#13161c]/45 font-semibold' : 'text-slate-600 border-[#ebedf1] font-medium'}`}
    >
      {icon}
      {label}
      {onRimuovi && (
        <button
          type="button"
          onClick={onRimuovi}
          className="inline-flex items-center justify-center w-4 h-4 rounded text-slate-400 hover:text-[#dc2626] hover:bg-[#dc2626]/[0.08]"
          title={`Togli ${label}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );

  return (
    <>
      {RUOLI_OPERATIVI.filter(r => utente.role === r.id).map(r =>
        chip(r.label, <r.icon className="h-3 w-3" />, undefined, 'Ruolo principale')
      )}
      {RUOLI_OPERATIVI.filter(r => extra.includes(r.id)).map(r =>
        chip(r.label, <r.icon className="h-3 w-3" />, () => onToggleRuoloExtra(r.id))
      )}
      {utente.is_publisher === true && <TagPublisher />}
      {tags.map(t => chip(t, null, () => onCambiaTag(tags.filter(x => x !== t))))}
      {aperto ? (
        <>
          <input
            autoFocus
            type="text"
            list={listaId}
            value={testo}
            onChange={e => setTesto(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); conferma(); }
              if (e.key === 'Escape') { setTesto(''); setAperto(false); }
            }}
            onBlur={conferma}
            placeholder="es. grafico"
            maxLength={30}
            className={`${campoTesto} h-7 w-32`}
          />
          <datalist id={listaId}>
            {RUOLI_OPERATIVI.filter(r => utente.role !== r.id && !extra.includes(r.id)).map(r => (
              <option key={r.id} value={r.label} />
            ))}
          </datalist>
        </>
      ) : (
        <button
          type="button"
          onClick={() => setAperto(true)}
          className="inline-flex items-center gap-1 h-7 px-2 rounded text-[11px] text-slate-400 border border-dashed border-[#dde0e6] hover:text-slate-700 hover:border-[#8b92a0] whitespace-nowrap transition-colors"
          title="Aggiungi un ruolo (Videomaker, Video Editor o un'etichetta libera)"
        >
          <Plus className="h-3 w-3" />
          ruolo
        </button>
      )}
    </>
  );
};

// Riga modificabile di una persona già attiva
const RigaPersona: React.FC<{
  utente: Utente;
  numClienti: number;
  onSalvaAnagrafica: (first_name: string, last_name: string) => void;
  onToggleRuoloExtra: (ruolo: RuoloOperativo) => void;
  onCambiaTag: (tags: string[]) => void;
}> = ({
  utente,
  numClienti,
  onSalvaAnagrafica,
  onToggleRuoloExtra,
  onCambiaTag,
}) => {
  const [nome, setNome] = useState(utente.first_name || '');
  const [cognome, setCognome] = useState(utente.last_name || '');

  const modificato =
    nome !== (utente.first_name || '') || cognome !== (utente.last_name || '');

  return (
    <div className={grigliaPersona}>
      <input
        type="text"
        value={nome}
        onChange={e => setNome(e.target.value)}
        placeholder="Nome"
        className={campoTesto}
      />
      <input
        type="text"
        value={cognome}
        onChange={e => setCognome(e.target.value)}
        placeholder="Cognome"
        className={campoTesto}
      />

      {/* Email con cui entra: chi non ha un accesso proprio usa l'account admin */}
      <div className="min-w-0 col-span-2 sm:col-span-1 flex items-center gap-1.5 text-[13px]">
        {utente.login_enabled === false ? (
          <Tag tenue title="Non ha un accesso proprio: entra con l'account amministratore">
            <KeyRound className="h-3 w-3" />
            entra con l'account admin
          </Tag>
        ) : (
          <span className="truncate text-slate-600" title={utente.email}>{utente.email}</span>
        )}
      </div>

      {/* Ruoli come tag: solo quelli che la persona ha, più «+ ruolo» */}
      <div className="flex items-center gap-1.5 flex-wrap col-span-2 sm:col-span-1">
        <RuoliPersona utente={utente} onToggleRuoloExtra={onToggleRuoloExtra} onCambiaTag={onCambiaTag} />
      </div>

      <div className="flex items-center gap-1.5 justify-end flex-wrap col-span-2 sm:col-span-1">
        <span className="text-xs text-slate-400 whitespace-nowrap min-w-[56px] text-right">
          {numClienti} {numClienti === 1 ? 'cliente' : 'clienti'}
        </span>
        {modificato && (
          <button onClick={() => onSalvaAnagrafica(nome, cognome)} className={bottonePrimario}>
            Salva
          </button>
        )}
      </div>
    </div>
  );
};

const AdminAssignments: React.FC = () => {
  const [clienti, setClienti] = useState<RigaCliente[]>([]);
  const [videomakers, setVideomakers] = useState<Utente[]>([]);
  const [editors, setEditors] = useState<Utente[]>([]);
  // Admin che lavorano anche sui clienti (Aldo): assegnabili in entrambi gli slot
  const [adminOperativi, setAdminOperativi] = useState<Utente[]>([]);
  const [inAttesa, setInAttesa] = useState<Utente[]>([]);
  const [stats, setStats] = useState<Statistiche | null>(null);
  const [tuttiUtenti, setTuttiUtenti] = useState<Utente[]>([]);
  const [mostraUtenti, setMostraUtenti] = useState(false);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState<string | null>(null);
  // Id della persona di cui si sta salvando il ruolo aggiuntivo publisher
  const [ricerca, setRicerca] = useState('');
  // Nuovo profilo senza accesso proprio (chi entra con l'account admin)
  const [nuovoProfilo, setNuovoProfilo] = useState<{
    aperto: boolean;
    first_name: string;
    last_name: string;
    role: RuoloOperativo;
  }>({ aperto: false, first_name: '', last_name: '', role: 'video_editor' });
  // Modifiche in corso sulle richieste in attesa, prima di premere Approva
  const [bozzaRichiesta, setBozzaRichiesta] = useState<
    Record<
      string,
      {
        first_name: string;
        last_name: string;
        role: RuoloOperativo;
        is_publisher: boolean;
        // Profilo senza accesso da dare a chi si è registrato ('' = account nuovo)
        profilo_id: string;
      }
    >
  >({});

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('bymarke_token')}`,
    'Content-Type': 'application/json',
  });

  const loadData = useCallback(async () => {
    try {
      const [matrixRes, vmRes, edRes, adminRes, pendingRes, statsRes, utentiRes] = await Promise.all([
        fetch(`${API_BASE_URL}/assignments/matrix`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/users/role/videomaker`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/users/role/video_editor`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/users/role/admin`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/users/pending`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/assignments/stats`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/users?limit=100`, { headers: getAuthHeaders() }),
      ]);

      const [matrix, vm, ed, adminList, pending, statistiche, utenti] = await Promise.all([
        matrixRes.json(),
        vmRes.json(),
        edRes.json(),
        adminRes.json(),
        pendingRes.json(),
        statsRes.json(),
        utentiRes.json(),
      ]);

      if (matrix.success) setClienti(matrix.data);
      if (vm.success) setVideomakers(vm.data);
      if (ed.success) setEditors(ed.data);
      // L'account admin condiviso non è una persona: non va offerto negli slot
      if (adminList.success)
        setAdminOperativi(
          (adminList.data as Utente[]).filter(u => u.username !== 'admin')
        );
      if (pending.success) setInAttesa(pending.data);
      if (statistiche.success) setStats(statistiche.data);
      if (utenti.success) setTuttiUtenti(utenti.data);
    } catch (error) {
      console.error('Errore nel caricamento:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Aggiorna uno slot del cliente. userId vuoto = slot liberato.
  const impostaSlot = async (clientId: string, slot: RuoloOperativo, userId: string) => {
    setSalvando(`${clientId}-${slot}`);

    const campo = slot === 'videomaker' ? 'videomaker_id' : 'video_editor_id';

    try {
      const response = await fetch(`${API_BASE_URL}/assignments/client/${clientId}/slots`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ [campo]: userId || null }),
      });

      const data = await response.json();

      if (data.success) {
        // Aggiorna solo la riga toccata: evita di far "saltare" la tabella
        setClienti(prev =>
          prev.map(riga => (riga.client_id === clientId ? { ...riga, ...data.data } : riga))
        );
        toast.success(userId ? 'Assegnazione aggiornata' : 'Assegnazione rimossa');

        // Le statistiche dipendono dagli slot coperti
        const statsRes = await fetch(`${API_BASE_URL}/assignments/stats`, {
          headers: getAuthHeaders(),
        });
        const statistiche = await statsRes.json();
        if (statistiche.success) setStats(statistiche.data);
      } else {
        toast.error(data.message || "Errore nell'assegnazione");
      }
    } catch (error) {
      console.error('Errore assegnazione:', error);
      toast.error("Errore nell'assegnazione");
    } finally {
      setSalvando(null);
    }
  };

  // Valori correnti del modulo di approvazione (quelli inviati in registrazione,
  // salvo le correzioni fatte dall'admin)
  // Profili senza accesso proprio: a uno di questi si può collegare chi si
  // registra, così riceve subito i clienti che segue già.
  const profiliSenzaAccesso = tuttiUtenti.filter(
    u => u.login_enabled === false && u.role !== 'admin'
  );
  const normaNome = (v?: string) =>
    (v || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z]/g, '');
  // Stesso nome (anche «Di Palo» = «Dipalo») e cognome di un profilo esistente: proposto già selezionato
  const profiloOmonimo = (utente: Utente) =>
    profiliSenzaAccesso.find(
      p =>
        normaNome(p.first_name) === normaNome(utente.first_name) &&
        normaNome(p.last_name) === normaNome(utente.last_name) &&
        !!normaNome(utente.first_name)
    );

  const datiRichiesta = (utente: Utente) => {
    if (bozzaRichiesta[utente.id]) return bozzaRichiesta[utente.id];
    const omonimo = profiloOmonimo(utente);
    return {
      first_name: utente.first_name || '',
      last_name: utente.last_name || '',
      role: (omonimo?.role as RuoloOperativo) || (utente.role as RuoloOperativo) || 'videomaker',
      is_publisher: utente.is_publisher === true,
      profilo_id: omonimo?.id || '',
    };
  };

  const aggiornaBozza = (
    utente: Utente,
    campo: 'first_name' | 'last_name' | 'role' | 'is_publisher' | 'profilo_id',
    valore: string | boolean
  ) =>
    setBozzaRichiesta(prev => ({
      ...prev,
      [utente.id]: { ...datiRichiesta(utente), [campo]: valore },
    }));

  const approvaUtente = async (utente: Utente) => {
    const dati = datiRichiesta(utente);

    if (!dati.first_name.trim() || !dati.last_name.trim()) {
      toast.error('Indica nome e cognome');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/users/${utente.id}/approve`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          role: dati.role,
          first_name: dati.first_name.trim(),
          last_name: dati.last_name.trim(),
          is_publisher: dati.is_publisher,
          ...(dati.profilo_id && !utente.will_be_admin ? { profilo_id: dati.profilo_id } : {}),
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(
          dati.profilo_id && !utente.will_be_admin
            ? `${dati.first_name} ${dati.last_name} può ora accedere e vede i clienti del suo profilo`
            : `${dati.first_name} ${dati.last_name} può ora accedere`
        );
        setBozzaRichiesta(prev => {
          const { [utente.id]: _rimosso, ...resto } = prev;
          return resto;
        });
        loadData();
      } else {
        toast.error(data.message || "Errore nell'approvazione");
      }
    } catch (error) {
      console.error('Errore approvazione:', error);
      toast.error("Errore nell'approvazione");
    }
  };

  const rifiutaUtente = async (utente: Utente) => {
    if (!window.confirm(`Rifiutare la richiesta di accesso di ${nomeUtente(utente)}?`)) return;

    try {
      const response = await fetch(`${API_BASE_URL}/users/${utente.id}/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Richiesta rifiutata');
        loadData();
      } else {
        toast.error(data.message || 'Errore nel rifiuto');
      }
    } catch (error) {
      console.error('Errore rifiuto:', error);
      toast.error('Errore nel rifiuto della richiesta');
    }
  };

  // Salva nome, cognome e ruolo di una persona già attiva
  const salvaPersona = async (
    utente: Utente,
    modifiche: { first_name?: string; last_name?: string; role?: RuoloOperativo; extra_roles?: RuoloOperativo[]; role_tags?: string[] }
  ) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${utente.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(modifiche),
      });

      const data = await response.json();

      if (data.success) {
        const liberati = data.data?.assignments_cleared || 0;
        toast.success(
          liberati > 0
            ? `Aggiornato: ${liberati} ${liberati === 1 ? 'cliente rimosso' : 'clienti rimossi'} dal ruolo precedente`
            : 'Dati aggiornati'
        );
        loadData();
      } else {
        toast.error(data.message || "Errore nell'aggiornamento");
      }
    } catch (error) {
      console.error('Errore aggiornamento utente:', error);
      toast.error("Errore nell'aggiornamento");
    }
  };

  const clientiFiltrati = clienti.filter(c =>
    c.company_name.toLowerCase().includes(ricerca.toLowerCase().trim())
  );

  // Quanti clienti ha in carico ciascuna persona
  const clientiDi = (userId: string) =>
    clienti.filter(
      c => c.videomaker?.user_id === userId || c.video_editor?.user_id === userId
    ).length;

  // Aggiunge un nome assegnabile per chi lavora sui clienti entrando con l'admin
  const creaProfiloSenzaAccesso = async () => {
    const { first_name, last_name, role } = nuovoProfilo;

    if (!first_name.trim() || !last_name.trim()) {
      toast.error('Indica nome e cognome');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/users/placeholder`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          first_name: first_name.trim(),
          last_name: last_name.trim(),
          role,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`${first_name.trim()} ${last_name.trim()} è ora assegnabile`);
        setNuovoProfilo({ aperto: false, first_name: '', last_name: '', role: 'video_editor' });
        loadData();
      } else {
        toast.error(data.message || 'Errore nella creazione del profilo');
      }
    } catch (error) {
      console.error('Errore creazione profilo:', error);
      toast.error('Errore nella creazione del profilo');
    }
  };

  const eliminaUtente = async (utente: Utente) => {
    const numClienti = clientiDi(utente.id);
    const avviso = numClienti > 0
      ? `\n\nPerderà anche le ${numClienti} assegnazioni cliente.`
      : '';

    if (
      !window.confirm(
        `Eliminare definitivamente ${nomeUtente(utente)} (${utente.email})?${avviso}\n\nL'operazione non è reversibile: potrà comunque registrarsi di nuovo con la stessa email.`
      )
    ) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/users/${utente.id}/permanent`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`${nomeUtente(utente)} eliminato`);
        loadData();
      } else {
        toast.error(data.message || "Errore nell'eliminazione");
      }
    } catch (error) {
      console.error('Errore eliminazione utente:', error);
      toast.error("Errore nell'eliminazione");
    }
  };

  // Cambio ruolo dalla lista persone: avvisa se comporta la perdita dei clienti
  // Aggiunge o toglie un ruolo extra. Togliendolo, i clienti assegnati in
  // quello slot vengono liberati dal backend: meglio chiederlo prima.
  const toggleRuoloExtra = (utente: Utente, ruolo: RuoloOperativo) => {
    const attuali = (utente.extra_roles ?? []) as RuoloOperativo[];
    const rimuove = attuali.includes(ruolo);
    const nuovi = rimuove ? attuali.filter(r => r !== ruolo) : [...attuali, ruolo];

    if (rimuove) {
      const nelloSlot = clienti.filter(c =>
        ruolo === 'videomaker'
          ? c.videomaker?.user_id === utente.id
          : c.video_editor?.user_id === utente.id
      ).length;
      if (
        nelloSlot > 0 &&
        !window.confirm(
          `Togliendo il ruolo, ${nomeUtente(utente)} perderà i ${nelloSlot} clienti assegnati come ${ruolo === 'videomaker' ? 'videomaker' : 'video editor'}. Procedere?`
        )
      ) {
        return;
      }
    }

    salvaPersona(utente, { extra_roles: nuovi });
  };

  // Chi copre due ruoli arriva sia tra i videomaker sia tra gli editor: una riga sola
  const membriTeam = [...videomakers, ...editors].filter(
    (u, i, arr) => arr.findIndex(x => x.id === u.id) === i
  );


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const statSlot = (valore: number, etichetta: string) => (
    <div className="flex items-baseline gap-1.5">
      <span className={`text-[15px] font-semibold ${valore > 0 ? 'text-[#b45309]' : 'text-slate-900'}`}>
        {valore}
      </span>
      <span className="text-xs text-slate-500">{etichetta}</span>
    </div>
  );

  return (
    <RoleGuard allowedRoles={['admin']} fallback={<AccessDenied />}>
      <div className="max-w-5xl mx-auto">
        {/* Testata: headlineMedium + bodySmall, come nel Team del Radar */}
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 mb-1">
          <h2 className="text-[21px] leading-tight">Gestione Ruoli</h2>
          {stats && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 pt-1">
              {statSlot(stats.clients_without_videomaker, 'senza videomaker')}
              {statSlot(stats.clients_without_editor, 'senza video editor')}
              <button
                type="button"
                onClick={() => setMostraUtenti(v => !v)}
                className={`${bottoneTesto} -mx-2`}
                title={mostraUtenti ? 'Chiudi elenco utenti' : 'Apri elenco utenti registrati'}
              >
                <Users className="h-3.5 w-3.5" />
                {mostraUtenti ? 'Chiudi utenti' : `Utenti · ${tuttiUtenti.length}`}
              </button>
            </div>
          )}
        </div>
        <p className="text-xs text-slate-500 max-w-2xl mb-6">
          Assegna a ogni cliente un videomaker e un video editor: vedranno solo i clienti assegnati.
          Il ruolo aggiuntivo Publisher, per chi pubblica i reel, dà accesso a tutti i clienti.
        </p>

        {/* Richieste di accesso in attesa */}
        {inAttesa.length > 0 && (
          <section className="mb-6">
            <SezioneTitolo
              titolo="Richieste di accesso"
              conteggio={inAttesa.length}
              sotto="Completa nome, cognome e ruolo, poi approva."
            />
            <Panel className="divide-y divide-[#ebedf1]">
              {inAttesa.map(utente => {
                const dati = datiRichiesta(utente);

                return (
                  <div key={utente.id} className="px-3 py-2">
                    <div className="flex items-center gap-2 mb-2 min-w-0">
                      <span className="text-xs text-slate-500 truncate">{utente.email}</span>
                      {utente.will_be_admin && (
                        <Tag>
                          <Shield className="h-3 w-3" />
                          amministratore
                        </Tag>
                      )}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_150px_280px] gap-2 items-center">
                      <input
                        type="text"
                        value={dati.first_name}
                        onChange={e => aggiornaBozza(utente, 'first_name', e.target.value)}
                        placeholder="Nome"
                        className={campoTesto}
                      />
                      <input
                        type="text"
                        value={dati.last_name}
                        onChange={e => aggiornaBozza(utente, 'last_name', e.target.value)}
                        placeholder="Cognome"
                        className={campoTesto}
                      />
                      <select
                        disabled={utente.will_be_admin}
                        value={dati.role}
                        onChange={e => aggiornaBozza(utente, 'role', e.target.value)}
                        className={`${campoTesto} disabled:opacity-50`}
                      >
                        <option value="videomaker">Videomaker</option>
                        <option value="video_editor">Video Editor</option>
                      </select>

                      {/* Il publisher non si assegna in approvazione: non passa da qui. */}

                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => approvaUtente(utente)} className={bottonePrimario}>
                          <UserCheck className="h-3.5 w-3.5" />
                          Approva
                        </button>
                        <button
                          onClick={() => rifiutaUtente(utente)}
                          className={`${bottoneIcona} hover:text-[#dc2626] hover:bg-[#dc2626]/[0.08]`}
                          title="Rifiuta richiesta"
                        >
                          <UserX className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {!utente.will_be_admin && profiliSenzaAccesso.length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="text-xs text-slate-500">Collega al profilo</span>
                        <select
                          value={dati.profilo_id}
                          onChange={e => {
                            const scelto = profiliSenzaAccesso.find(p => p.id === e.target.value);
                            setBozzaRichiesta(prev => ({
                              ...prev,
                              [utente.id]: {
                                ...datiRichiesta(utente),
                                profilo_id: e.target.value,
                                ...(scelto
                                  ? {
                                      // Vale il nome scritto in registrazione; quello
                                      // del profilo solo se il campo è vuoto
                                      first_name: datiRichiesta(utente).first_name || scelto.first_name || '',
                                      last_name: datiRichiesta(utente).last_name || scelto.last_name || '',
                                      role: (scelto.role as RuoloOperativo) || datiRichiesta(utente).role,
                                    }
                                  : {}),
                              },
                            }));
                          }}
                          className={`${campoTesto} sm:w-[280px]`}
                        >
                          <option value="">Nessuno: account nuovo, senza clienti</option>
                          {profiliSenzaAccesso.map(p => (
                            <option key={p.id} value={p.id}>
                              {nomeUtente(p)} · {p.role === 'video_editor' ? 'Video Editor' : 'Videomaker'}
                            </option>
                          ))}
                        </select>
                        <span className="text-xs text-slate-400">
                          {dati.profilo_id
                            ? 'Entrerà con la sua email e vedrà solo i clienti di quel profilo.'
                            : 'Riceverà i clienti da Gestione Ruoli.'}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </Panel>
          </section>
        )}

        {/* Elenco utenti registrati, aperto da "Utenti" in testata */}
        {mostraUtenti && (
          <section className="mb-6">
            <SezioneTitolo titolo="Utenti registrati" conteggio={tuttiUtenti.length} />
            <Panel className="divide-y divide-[#ebedf1]">
              {tuttiUtenti.length === 0 ? (
                <div className="text-center py-6 text-sm text-slate-400">Nessun utente registrato</div>
              ) : (
                tuttiUtenti.map(utente => {
                  const isAdmin = utente.role === 'admin';
                  const numClienti = clientiDi(utente.id);

                  return (
                    <div key={utente.id} className="flex items-center gap-3 px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-semibold text-slate-900 truncate">
                            {nomeUtente(utente)}
                          </span>
                          {isAdmin && (
                            <Tag tenue>
                              <Shield className="h-3 w-3" />
                              admin
                            </Tag>
                          )}
                          {utente.is_publisher && <TagPublisher />}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {utente.login_enabled === false
                            ? 'entra con l’account admin'
                            : utente.email}
                        </div>
                      </div>

                      <span className="text-xs text-slate-400 whitespace-nowrap hidden sm:inline">
                        {isAdmin || utente.is_publisher
                          ? 'tutti i clienti'
                          : `${numClienti} ${numClienti === 1 ? 'cliente' : 'clienti'}`}
                      </span>

                      {isAdmin ? (
                        <span className={`${bottoneIcona} text-slate-300 hover:bg-transparent hover:text-slate-300 cursor-default`} title="Gli account admin non si eliminano">
                          <Trash2 className="h-4 w-4" />
                        </span>
                      ) : (
                        <button
                          onClick={() => eliminaUtente(utente)}
                          className={`${bottoneIcona} hover:text-[#dc2626] hover:bg-[#dc2626]/[0.08]`}
                          title="Elimina definitivamente"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </Panel>
          </section>
        )}

        {/* Persone attive: nome, cognome e ruolo modificabili */}
        <section className="mb-6">
          <SezioneTitolo
            titolo="Persone del team"
            conteggio={membriTeam.length}
            sotto="Nome, cognome e ruolo. Chi entra con l'account admin non ha un profilo proprio: lo aggiungi qui per poterlo assegnare ai clienti."
            destra={
              !nuovoProfilo.aperto ? (
                <button
                  onClick={() => setNuovoProfilo(p => ({ ...p, aperto: true }))}
                  className={bottonePrimario}
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  Aggiungi
                </button>
              ) : undefined
            }
          />
          <Panel className="divide-y divide-[#ebedf1]">
            <div className="hidden sm:grid grid-cols-[1fr_1fr_1.3fr_auto_150px] gap-2 px-3 pt-2 pb-1">
              <Eyebrow>Nome</Eyebrow>
              <Eyebrow>Cognome</Eyebrow>
              <Eyebrow>Email di accesso</Eyebrow>
              <Eyebrow>Ruoli</Eyebrow>
              <span />
            </div>
            {membriTeam.map(utente => (
              <RigaPersona
                key={utente.id}
                utente={utente}
                numClienti={clientiDi(utente.id)}
                onSalvaAnagrafica={(nome, cognome) =>
                  salvaPersona(utente, { first_name: nome, last_name: cognome })
                }
                onToggleRuoloExtra={ruolo => toggleRuoloExtra(utente, ruolo)}
                onCambiaTag={tags => salvaPersona(utente, { role_tags: tags })}
              />
            ))}

            {nuovoProfilo.aperto && (
              <div className={`${grigliaPersona} bg-[#f4f5f7]/60`}>
                <input
                  type="text"
                  autoFocus
                  value={nuovoProfilo.first_name}
                  onChange={e => setNuovoProfilo(p => ({ ...p, first_name: e.target.value }))}
                  placeholder="Nome"
                  className={campoTesto}
                />
                <input
                  type="text"
                  value={nuovoProfilo.last_name}
                  onChange={e => setNuovoProfilo(p => ({ ...p, last_name: e.target.value }))}
                  placeholder="Cognome"
                  className={campoTesto}
                />
                <div className="col-span-2 sm:col-span-1 text-xs text-slate-400">entra con l'account admin</div>
                <select
                  value={nuovoProfilo.role}
                  onChange={e =>
                    setNuovoProfilo(p => ({ ...p, role: e.target.value as RuoloOperativo }))
                  }
                  className={campoTesto}
                >
                  <option value="videomaker">Videomaker</option>
                  <option value="video_editor">Video Editor</option>
                </select>
                <div className="flex items-center gap-1 justify-end col-span-2 sm:col-span-1">
                  <button onClick={creaProfiloSenzaAccesso} className={bottonePrimario}>
                    Aggiungi
                  </button>
                  <button
                    onClick={() =>
                      setNuovoProfilo({
                        aperto: false,
                        first_name: '',
                        last_name: '',
                        role: 'video_editor',
                      })
                    }
                    className={bottoneIcona}
                    title="Annulla"
                  >
                    <UserX className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </Panel>
        </section>

        {/* Assegnazioni per cliente */}
        <section>
          <SezioneTitolo
            titolo="Clienti attivi"
            conteggio={clientiFiltrati.length}
            destra={
              <div className="relative w-full sm:w-56">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input
                  type="text"
                  value={ricerca}
                  onChange={e => setRicerca(e.target.value)}
                  placeholder="Cerca cliente..."
                  className={`${campoTesto} pl-8`}
                />
              </div>
            }
          />

          <Panel>
            {clientiFiltrati.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400">Nessun cliente trovato</div>
            ) : (
              <>
                {/* Mobile: una riga per cliente, i due slot impilati */}
                <div className="block md:hidden divide-y divide-[#ebedf1]">
                  {clientiFiltrati.map(riga => (
                    <div key={riga.client_id} className="px-3 py-2.5">
                      <div className="text-sm font-semibold text-slate-900 truncate mb-2">
                        {riga.company_name}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="mb-1"><Eyebrow>Videomaker</Eyebrow></div>
                          <SelectSlot
                            riga={riga}
                            slot="videomaker"
                            opzioni={videomakers}
                            admin={adminOperativi}
                            inCorso={salvando === `${riga.client_id}-videomaker`}
                            onChange={userId => impostaSlot(riga.client_id, 'videomaker', userId)}
                          />
                        </div>
                        <div>
                          <div className="mb-1"><Eyebrow>Video Editor</Eyebrow></div>
                          <SelectSlot
                            riga={riga}
                            slot="video_editor"
                            opzioni={editors}
                            admin={adminOperativi}
                            inCorso={salvando === `${riga.client_id}-video_editor`}
                            onChange={userId => impostaSlot(riga.client_id, 'video_editor', userId)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop: tabella compatta */}
                <table className="hidden md:table w-full">
                  <thead>
                    <tr className="text-left">
                      <th className="px-3 pt-2 pb-1 font-normal"><Eyebrow>Cliente</Eyebrow></th>
                      <th className="px-3 pt-2 pb-1 w-[30%] font-normal">
                        <Eyebrow><span className="inline-flex items-center gap-1"><Video className="h-3 w-3" />Videomaker</span></Eyebrow>
                      </th>
                      <th className="px-3 pt-2 pb-1 w-[30%] font-normal">
                        <Eyebrow><span className="inline-flex items-center gap-1"><Scissors className="h-3 w-3" />Video Editor</span></Eyebrow>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#ebedf1]">
                    {clientiFiltrati.map(riga => (
                      <tr key={riga.client_id} className="hover:bg-[#f0f1f4]/45 transition-colors">
                        <td className="px-3 py-1.5 text-[13px] font-semibold text-slate-900">
                          {riga.company_name}
                        </td>
                        <td className="px-3 py-1.5">
                          <SelectSlot
                            riga={riga}
                            slot="videomaker"
                            opzioni={videomakers}
                            admin={adminOperativi}
                            inCorso={salvando === `${riga.client_id}-videomaker`}
                            onChange={userId => impostaSlot(riga.client_id, 'videomaker', userId)}
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <SelectSlot
                            riga={riga}
                            slot="video_editor"
                            opzioni={editors}
                            admin={adminOperativi}
                            inCorso={salvando === `${riga.client_id}-video_editor`}
                            onChange={userId => impostaSlot(riga.client_id, 'video_editor', userId)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </Panel>
        </section>

        {(videomakers.length === 0 || editors.length === 0) && (
          <p className="mt-4 text-xs text-slate-400 text-center">
            {videomakers.length === 0 && editors.length === 0
              ? 'Nessun videomaker o video editor approvato: le tendine restano vuote finché non approvi le registrazioni.'
              : videomakers.length === 0
                ? 'Nessun videomaker approvato al momento.'
                : 'Nessun video editor approvato al momento.'}
          </p>
        )}
      </div>
    </RoleGuard>
  );
};

export default AdminAssignments;
